import React, { useEffect, useMemo, useState, useCallback } from "react";
import { apiFetch } from "../utils/api";
import { useFeedback } from "../hooks/useFeedback";
import { IconTrash } from "./ui/icons";
import ConfirmDelete from "./ui/ConfirmDelete";
import CollapsibleCard from "./ui/CollapsibleCard";

const VISIBLE_STEP = 15;

function IconSearch() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-gray-400">
      <path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clipRule="evenodd" />
    </svg>
  );
}

function UtcHint({ startUtc, endUtc }) {
  const [open, setOpen] = useState(false);
  if (!startUtc && !endUtc) return null;
  return (
    <div className="relative inline-block">
      <button
        type="button"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        className="text-[10px] text-gray-400 hover:text-gray-600 underline underline-offset-2 cursor-help"
      >
        UTC ⓘ
      </button>
      {open && (
        <div className="absolute bottom-full left-0 mb-1 z-50 bg-gray-800 text-white text-[10px] rounded px-2 py-1.5 whitespace-nowrap shadow-lg">
          <div>Début : {startUtc ?? "—"}</div>
          <div>Fin    : {endUtc ?? "—"}</div>
        </div>
      )}
    </div>
  );
}

const isNA = (f) =>
  Array.isArray(f.assignments) &&
  f.assignments.length > 0 &&
  f.assignments.every(a => a.site_id == null);

function AssignmentRow({ a, index, formId, sites, onUpdate, onDelete, onValidate }) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    await onDelete(formId, index);
    setDeleting(false);
    setConfirmDelete(false);
  };

  return (
    <div className="rounded-lg border border-gray-100 bg-gray-50 p-2.5 flex flex-col gap-1.5">
      <div className="grid grid-cols-12 gap-2 items-end">
        <div className="col-span-12 sm:col-span-5">
          <label className="block text-[10px] text-gray-500 mb-0.5">Site</label>
          <select
            className="input py-1.5 text-xs"
            value={a.site_id ?? ""}
            onChange={e => {
              const val = e.target.value || null;
              const site = sites.find(s => s.site_id === val);
              onUpdate(index, { site_id: val, site_label: site?.label ?? "N/A" });
            }}
          >
            {sites.map((s, i) => (
              <option key={i} value={s.site_id ?? ""}>
                {s.label}{s.paused ? " (pause)" : ""}
              </option>
            ))}
          </select>
        </div>

        <div className="col-span-5 sm:col-span-3">
          <label className="block text-[10px] text-gray-500 mb-0.5">Début</label>
          <input
            type="date"
            className="input py-1.5 text-xs"
            value={a.start_local ?? ""}
            onChange={e => onUpdate(index, { start_local: e.target.value || null, start_utc: null, end_utc: null })}
          />
        </div>

        <div className="col-span-5 sm:col-span-3">
          <label className="block text-[10px] text-gray-500 mb-0.5">Fin</label>
          <input
            type="date"
            className="input py-1.5 text-xs"
            value={a.end_local ?? ""}
            onChange={e => onUpdate(index, { end_local: e.target.value || null, start_utc: null, end_utc: null })}
          />
        </div>

        <div className="col-span-2 sm:col-span-1 flex flex-col gap-1 items-center">
          <label className="block text-[10px] text-gray-500 mb-0.5">État</label>
          <button
            type="button"
            onClick={() => onValidate(index, !a.validated)}
            className={`btn btn-icon btn-sm w-full text-xs font-semibold ${a.validated ? "bg-emerald-100 text-emerald-700 border border-emerald-200" : "bg-gray-100 text-gray-500 border border-gray-200"}`}
          >
            {a.validated ? "✓" : "—"}
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <UtcHint startUtc={a.start_utc} endUtc={a.end_utc} />
        {!confirmDelete ? (
          <button
            type="button"
            onClick={() => setConfirmDelete(true)}
            className="btn btn-icon btn-ghost text-red-400 hover:text-red-600 hover:bg-red-50"
          >
            <IconTrash />
          </button>
        ) : (
          <ConfirmDelete
            onConfirm={handleDelete}
            onCancel={() => setConfirmDelete(false)}
            loading={deleting}
          />
        )}
      </div>
    </div>
  );
}

function FormCard({ f, sites, dirty, onUpdate, onSave, onDelete, onValidate, saving }) {
  const hasFull = (f.assignments || []).some(a => a.start_local == null && a.end_local == null);

  const addAssignment = (isFull) => {
    const newA = {
      site_id: null, site_label: "N/A",
      start_local: isFull ? null : "",
      end_local:   isFull ? null : "",
      start_utc: null, end_utc: null, validated: false,
    };
    onUpdate(f.form_id, form => ({ ...form, assignments: [...(form.assignments || []), newA] }));
  };

  return (
    <div className={`rounded-xl border p-4 transition-colors ${dirty ? "border-amber-300 bg-amber-50/30" : "border-gray-100 bg-white"}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="font-semibold text-sm text-gray-800 truncate">{f.form_name}</div>
          <div className="text-xs text-gray-400 font-mono">#{f.form_id}</div>
          <div className="flex items-center gap-1.5 mt-1">
            <span className={`badge ${f.status === "validated" ? "badge-active" : "badge-neutral"}`}>
              {f.status === "validated" ? "validé" : "brouillon"}
            </span>
            {dirty && <span className="badge badge-paused">modifié</span>}
          </div>
        </div>
        <button
          onClick={() => onSave(f)}
          disabled={saving}
          className={`btn btn-sm shrink-0 ${dirty ? "btn-primary" : "btn-secondary"}`}
        >
          {saving ? "Enregistrement…" : dirty ? "Enregistrer ✦" : "Enregistré"}
        </button>
      </div>

      <div className="flex flex-wrap gap-1.5 mb-3">
        <button
          onClick={() => addAssignment(true)}
          disabled={hasFull}
          className={`btn btn-sm ${hasFull ? "btn-secondary opacity-50 cursor-not-allowed" : "btn-success"}`}
        >
          {hasFull ? "Période complète présente" : "+ Période complète"}
        </button>
        <button onClick={() => addAssignment(false)} className="btn btn-sm btn-secondary">
          + Changement en cours de saison
        </button>
      </div>

      <div className="flex flex-col gap-2">
        {(f.assignments || []).length === 0 && (
          <p className="text-xs text-gray-400 py-1">Aucune période — ajoutez-en une ci-dessus.</p>
        )}
        {(f.assignments || []).map((a, i) => (
          <AssignmentRow
            key={i}
            a={a}
            index={i}
            formId={f.form_id}
            sites={sites}
            onUpdate={(idx, patch) =>
              onUpdate(f.form_id, form => {
                const arr = [...(form.assignments || [])];
                arr[idx] = { ...arr[idx], ...patch };
                return { ...form, assignments: arr };
              })
            }
            onDelete={onDelete}
            onValidate={(idx, val) => onValidate(f.form_id, idx, val)}
          />
        ))}
      </div>
    </div>
  );
}

export default function AssociationsEditor() {
  const [sites, setSites]           = useState([]);
  const [kizeoForms, setKizeoForms] = useState([]);
  const [doc, setDoc]               = useState(null);
  const [loading, setLoading]       = useState(true);
  const [savingId, setSavingId]     = useState(null);
  const [dirtyIds, setDirtyIds]     = useState(new Set());
  const [search, setSearch]         = useState("");
  const [showNA, setShowNA]         = useState(false);
  const [visibleCount, setVisibleCount] = useState(VISIBLE_STEP);
  const [feedback, showFeedback]    = useFeedback();

  useEffect(() => {
    (async () => {
      try {
        const [sData, kForms, map] = await Promise.all([
          apiFetch("/api/assoc/sites"),
          apiFetch("/api/assoc/kizeo-forms"),
          apiFetch("/api/assoc/forms-sites"),
        ]);
        setSites(Array.isArray(sData) ? sData : []);
        setKizeoForms(kForms);
        setDoc(map);
      } catch (e) {
        showFeedback("error", "Erreur de chargement : " + e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const byFormId = useMemo(() => {
    const m = new Map();
    for (const f of (doc?.forms || [])) m.set(f.form_id, f);
    return m;
  }, [doc]);

  const rows = useMemo(() => {
    const ids = new Set(kizeoForms.map(f => f.form_id));
    const base = kizeoForms.map(f => ({
      form_id:     f.form_id,
      form_name:   f.form_name || String(f.form_id),
      status:      byFormId.get(f.form_id)?.status || "draft",
      assignments: byFormId.get(f.form_id)?.assignments || [],
      notes:       byFormId.get(f.form_id)?.notes || "",
    }));
    for (const f of (doc?.forms || [])) {
      if (!ids.has(f.form_id)) {
        base.push({ form_id: f.form_id, form_name: f.form_name || String(f.form_id), status: f.status || "draft", assignments: f.assignments || [], notes: f.notes || "" });
      }
    }
    return base.sort((a, b) => a.form_name.localeCompare(b.form_name));
  }, [kizeoForms, byFormId, doc]);

  const { activeRows, naRows } = useMemo(() => {
    const q = search.trim().toLowerCase();
    const match = f => !q || f.form_name.toLowerCase().includes(q) || String(f.form_id).includes(q);
    const act = [], na = [];
    for (const f of rows) {
      if (!match(f)) continue;
      (isNA(f) ? na : act).push(f);
    }
    return { activeRows: act, naRows: na };
  }, [rows, search]);

  const updateForm = useCallback((form_id, updater) => {
    setDoc(prev => {
      const copy = { ...prev, forms: [...(prev?.forms || [])] };
      const idx = copy.forms.findIndex(f => f.form_id === form_id);
      const blank = { form_id, form_name: String(form_id), status: "draft", assignments: [], notes: "" };
      if (idx === -1) copy.forms.push(updater(blank));
      else copy.forms[idx] = updater({ ...copy.forms[idx] });
      return copy;
    });
    setDirtyIds(prev => { const next = new Set(prev); next.add(form_id); return next; });
  }, []);

  const saveForm = async (form) => {
    setSavingId(form.form_id);
    try {
      const clean = { ...form, assignments: (form.assignments || []).map(a => ({ ...a, start_local: a.start_local || null, end_local: a.end_local || null })) };
      const newDoc = await apiFetch("/api/assoc/form", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ form: clean, updated_by: "ui" }),
      });
      setDoc(newDoc);
      setDirtyIds(prev => { const next = new Set(prev); next.delete(form.form_id); return next; });
    } catch (e) {
      showFeedback("error", "Erreur : " + e.message);
    } finally {
      setSavingId(null);
    }
  };

  const handleValidate = async (form_id, index, validated) => {
    const currentForm = rows.find(r => r.form_id === form_id);
    if (currentForm && (dirtyIds.has(form_id) || !byFormId.has(form_id))) {
      await saveForm(currentForm);
    }
    try {
      const newDoc = await apiFetch("/api/assoc/validate-assignment", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ form_id, index, validated }),
      });
      setDoc(newDoc);
    } catch (e) {
      showFeedback("error", "Erreur validation : " + e.message);
    }
  };

  const handleDeleteAssignment = async (form_id, index) => {
    try {
      const newDoc = await apiFetch(`/api/assoc/assignment?form_id=${form_id}&index=${index}`, { method: "DELETE" });
      setDoc(newDoc);
    } catch (e) {
      showFeedback("error", "Erreur suppression : " + e.message);
    }
  };

  const shownActive = search ? activeRows : activeRows.slice(0, visibleCount);
  const hasMore = !search && activeRows.length > visibleCount;

  return (
    <CollapsibleCard
      icon="🔗"
      title="Association Formulaire ↔ Site"
      badge={!loading ? `${rows.length} form.` : null}
      headerRight={doc?.meta?.updated_at ? (
        <span className="text-xs text-gray-400">
          Mise à jour : {new Date(doc.meta.updated_at).toLocaleString("fr-FR", { timeZone: "Europe/Paris" })}
        </span>
      ) : null}
    >
      {loading && (
        <div className="px-4 py-6 text-sm text-gray-400 text-center">Chargement des associations…</div>
      )}

      {!loading && <>
      {feedback && (
        <div className={`mx-4 mt-3 feedback-${feedback.type === "success" ? "success" : "error"}`}>
          {feedback.msg}
        </div>
      )}

      <div className="px-4 pt-3 pb-2">
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2"><IconSearch /></span>
          <input
            type="text"
            className="input pl-9 text-sm"
            placeholder={`Rechercher parmi ${activeRows.length + naRows.length} formulaires…`}
            value={search}
            onChange={e => { setSearch(e.target.value); setVisibleCount(VISIBLE_STEP); }}
          />
          {search && (
            <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 text-lg leading-none">×</button>
          )}
        </div>
        {search && (
          <p className="text-xs text-gray-400 mt-1">
            {activeRows.length + naRows.length} résultat{activeRows.length + naRows.length > 1 ? "s" : ""}
          </p>
        )}
      </div>

      <div className="px-4 pb-2 flex flex-col gap-3 max-h-[70vh] overflow-y-auto">
        {shownActive.length === 0 && (
          <p className="text-sm text-gray-400 py-4 text-center">
            {search ? `Aucun résultat pour « ${search} ».` : "Aucun formulaire actif."}
          </p>
        )}
        {shownActive.map(f => (
          <FormCard
            key={f.form_id}
            f={f}
            sites={sites}
            dirty={dirtyIds.has(f.form_id)}
            saving={savingId === f.form_id}
            onUpdate={updateForm}
            onSave={saveForm}
            onDelete={handleDeleteAssignment}
            onValidate={handleValidate}
          />
        ))}
        {hasMore && (
          <button
            onClick={() => setVisibleCount(v => v + VISIBLE_STEP)}
            className="btn btn-md btn-secondary w-full"
          >
            Afficher {Math.min(VISIBLE_STEP, activeRows.length - visibleCount)} de plus
            <span className="text-gray-400 ml-1">({activeRows.length - visibleCount} restants)</span>
          </button>
        )}
      </div>

      <div className="border-t border-gray-100">
        <button
          onClick={() => setShowNA(v => !v)}
          className="w-full flex items-center justify-between px-4 py-3 text-sm hover:bg-gray-50 transition-colors"
        >
          <span className="font-medium text-gray-600">Formulaires N/A</span>
          <span className="flex items-center gap-2">
            <span className="badge badge-neutral">{naRows.length}</span>
            <span className="text-gray-400">{showNA ? "▲" : "▼"}</span>
          </span>
        </button>
        {showNA && (
          <div className="px-4 pb-4 flex flex-col gap-3 max-h-[40vh] overflow-y-auto">
            {naRows.length === 0
              ? <p className="text-sm text-gray-400 py-2">Aucun formulaire N/A.</p>
              : naRows.map(f => (
                  <FormCard
                    key={f.form_id}
                    f={f}
                    sites={sites}
                    dirty={dirtyIds.has(f.form_id)}
                    saving={savingId === f.form_id}
                    onUpdate={updateForm}
                    onSave={saveForm}
                    onDelete={handleDeleteAssignment}
                    onValidate={handleValidate}
                  />
                ))
            }
          </div>
        )}
      </div>
      </>}
    </CollapsibleCard>
  );
}
